import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import api from '../../services/api';
import {
  fetchUtilisateurs, createUtilisateur, updateUtilisateur,
  supprimerUtilisateur, suspendreUtilisateur, activerUtilisateur,
  reinitialiserMdp,
} from '../../store/slices/utilisateursSlice';
import {
  Plus, Search, Pencil, Trash2, UserCheck, UserX,
  KeyRound, Users, Shield, Briefcase, AlertTriangle, Eye, EyeOff, Save,
} from 'lucide-react';

/* ── Constantes ── */
const ROLES = {
  administrateur: { label: 'Administrateur', badge: 'bleu',  Icon: Shield    },
  commercial:     { label: 'Commercial',     badge: 'vert',  Icon: Briefcase },
};

const STATUTS = {
  actif:    { label: 'Actif',    badge: 'vert'   },
  suspendu: { label: 'Suspendu', badge: 'rouge'  },
  inactif:  { label: 'Inactif',  badge: 'gris'   },
};

const EMPTY_FORM = {
  nom_util: '', prenom_util: '', email: '', telephone: '',
  role: 'commercial', statut: 'actif',
  password: '', confirm_password: '',
};

const fmt = (d) => d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

/* ── Styles compacts partagés ── */
const S = {
  card:   { margin:0, padding:'10px 14px', display:'flex', flexDirection:'column',
            background:'#F9FAFB', borderRadius:8, border:'1px solid #E5E7EB' },
  head:   { display:'flex', alignItems:'center', gap:7, paddingBottom:8,
            borderBottom:'1px solid #E5E7EB', marginBottom:10, flexShrink:0 },
  htitle: { fontSize:11, fontWeight:700, color:'#1F4E79', margin:0,
            textTransform:'uppercase', letterSpacing:'0.05em' },
  label:  { display:'block', fontSize:11, fontWeight:600, color:'#374151', marginBottom:3 },
  grid2:  { display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 },
  fg:     { margin:0 },
  err:    { color:'#C00000', fontSize:10, marginTop:2 },
  inp:    { fontSize:12, padding:'6px 10px' },
};

/* ══════════════════════════════════
   MODAL CRÉATION / ÉDITION
══════════════════════════════════ */
function ModalUtilisateur({ utilisateur, onClose, onSaved }) {
  const dispatch  = useDispatch();
  const isEdit    = Boolean(utilisateur);
  const [form,    setForm]    = useState(isEdit ? {
    nom_util: utilisateur.nom_util, prenom_util: utilisateur.prenom_util,
    email: utilisateur.email, telephone: utilisateur.telephone || '',
    role: utilisateur.role, statut: utilisateur.statut,
    password: '', confirm_password: '',
  } : { ...EMPTY_FORM });
  const [errors,      setErrors]      = useState({});
  const [submitting,  setSubmitting]  = useState(false);
  const [showPw,      setShowPw]      = useState(false);
  const [showCpw,     setShowCpw]     = useState(false);

  const handle = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    setErrors(er => ({ ...er, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.nom_util.trim())    errs.nom_util    = 'Obligatoire';
    if (!form.prenom_util.trim()) errs.prenom_util = 'Obligatoire';
    if (!form.email.trim())       errs.email       = 'Obligatoire';
    if (!isEdit && !form.password) errs.password   = 'Obligatoire pour un nouveau compte';
    if (form.password && form.password !== form.confirm_password)
      errs.confirm_password = 'Les mots de passe ne correspondent pas';
    if (form.password && form.password.length < 8)
      errs.password = 'Minimum 8 caractères';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.password) { delete payload.password; delete payload.confirm_password; }

      if (isEdit) {
        await dispatch(updateUtilisateur({ id: utilisateur.id, payload })).unwrap();
        toast.success('Utilisateur modifié avec succès.');
      } else {
        await dispatch(createUtilisateur(payload)).unwrap();
        toast.success('Utilisateur créé avec succès.');
      }
      onSaved();
    } catch (err) {
      if (err && typeof err === 'object') {
        const mapped = {};
        Object.entries(err).forEach(([k, v]) => {
          mapped[k] = Array.isArray(v) ? v[0] : v;
        });
        setErrors(mapped);
        toast.error('Corrigez les erreurs dans le formulaire.');
      } else {
        toast.error("Erreur lors de l'enregistrement.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:12, padding:20, width:'100%', maxWidth:640,
        boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
          <Users size={18} color="#1F4E79" />
          <h2 style={{ fontSize:15, fontWeight:700, color:'#1F4E79', margin:0 }}>
            {isEdit ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}
          </h2>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>

            {/* Col 1 : Identité */}
            <div style={S.card}>
              <div style={S.head}>
                <Users size={13} color="#1F4E79"/>
                <span style={S.htitle}>Identité</span>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                <div style={S.grid2}>
                  <div style={S.fg}>
                    <label style={S.label}>Prénom *</label>
                    <input className="form-control" style={S.inp} name="prenom_util"
                      value={form.prenom_util} onChange={handle}
                      placeholder="Ex : Kadiatou" autoFocus />
                    {errors.prenom_util && <p style={S.err}>{errors.prenom_util}</p>}
                  </div>
                  <div style={S.fg}>
                    <label style={S.label}>Nom *</label>
                    <input className="form-control" style={S.inp} name="nom_util"
                      value={form.nom_util} onChange={handle} placeholder="Ex : DIALLO" />
                    {errors.nom_util && <p style={S.err}>{errors.nom_util}</p>}
                  </div>
                </div>
                <div style={S.grid2}>
                  <div style={S.fg}>
                    <label style={S.label}>Email *</label>
                    <input className="form-control" style={S.inp} type="email" name="email"
                      value={form.email} onChange={handle} placeholder="exemple@crm.com" />
                    {errors.email && <p style={S.err}>{errors.email}</p>}
                  </div>
                  <div style={S.fg}>
                    <label style={S.label}>Téléphone</label>
                    <input className="form-control" style={S.inp} name="telephone"
                      value={form.telephone} onChange={handle} placeholder="+225 07 00 00 00 00" />
                    {errors.telephone && <p style={S.err}>{errors.telephone}</p>}
                  </div>
                </div>
                <div style={S.grid2}>
                  <div style={S.fg}>
                    <label style={S.label}>Rôle</label>
                    <select className="form-control" style={S.inp} name="role"
                      value={form.role} onChange={handle}>
                      <option value="commercial">Commercial</option>
                      <option value="administrateur">Administrateur</option>
                    </select>
                    {errors.role && <p style={S.err}>{errors.role}</p>}
                  </div>
                  {isEdit && (
                    <div style={S.fg}>
                      <label style={S.label}>Statut</label>
                      <select className="form-control" style={S.inp} name="statut"
                        value={form.statut} onChange={handle}>
                        <option value="actif">Actif</option>
                        <option value="suspendu">Suspendu</option>
                        <option value="inactif">Inactif</option>
                      </select>
                      {errors.statut && <p style={S.err}>{errors.statut}</p>}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Col 2 : Sécurité */}
            <div style={S.card}>
              <div style={S.head}>
                <KeyRound size={13} color="#1F4E79"/>
                <span style={S.htitle}>Sécurité</span>
              </div>
              <p style={{ fontSize:11, color:'#6B7280', margin:'0 0 10px', lineHeight:1.5 }}>
                {isEdit
                  ? 'Laisser vide pour ne pas modifier le mot de passe.'
                  : 'Mot de passe obligatoire pour un nouveau compte.'}
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                <div style={S.fg}>
                  <label style={S.label}>Mot de passe {!isEdit && '*'}</label>
                  <div style={{ position:'relative' }}>
                    <input className="form-control" style={{ ...S.inp, paddingRight:32 }}
                      type={showPw ? 'text' : 'password'}
                      name="password" value={form.password} onChange={handle}
                      placeholder="Min. 8 caractères" />
                    <button type="button" onClick={() => setShowPw(v=>!v)}
                      style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)',
                        background:'none', border:'none', cursor:'pointer', color:'#6B7280', padding:0 }}>
                      {showPw ? <EyeOff size={14}/> : <Eye size={14}/>}
                    </button>
                  </div>
                  {errors.password && <p style={S.err}>{errors.password}</p>}
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Confirmer {!isEdit && '*'}</label>
                  <div style={{ position:'relative' }}>
                    <input className="form-control" style={{ ...S.inp, paddingRight:32 }}
                      type={showCpw ? 'text' : 'password'}
                      name="confirm_password" value={form.confirm_password} onChange={handle}
                      placeholder="Répéter le mot de passe" />
                    <button type="button" onClick={() => setShowCpw(v=>!v)}
                      style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)',
                        background:'none', border:'none', cursor:'pointer', color:'#6B7280', padding:0 }}>
                      {showCpw ? <EyeOff size={14}/> : <Eye size={14}/>}
                    </button>
                  </div>
                  {errors.confirm_password && <p style={S.err}>{errors.confirm_password}</p>}
                </div>
              </div>
            </div>

          </div>

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:12 }}>
            <button type="button" className="header-btn btn-secondary"
              onClick={onClose} style={{ padding:'8px 18px', fontSize:13 }}>
              Annuler
            </button>
            <button type="submit" className="header-btn btn-primary" disabled={submitting}
              style={{ padding:'8px 22px', fontSize:13, fontWeight:700 }}>
              {submitting ? 'Enregistrement…' : (isEdit ? 'Enregistrer' : 'Créer le compte')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   MODAL RESET MDP — affiche le MDP généré
══════════════════════════════════ */
function ModalMdpGenere({ nom, mdp, onClose }) {
  const [visible, setVisible] = useState(false);
  const copier = () => {
    navigator.clipboard.writeText(mdp).then(() => toast.success('Mot de passe copié !'));
  };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1100,
      display:'flex', alignItems:'center', justifyContent:'center' }} onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:12, padding:28, width:'100%', maxWidth:420,
        boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>

        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
          <KeyRound size={20} color="#1F4E79" />
          <h2 style={{ fontSize:16, fontWeight:700, color:'#1F4E79', margin:0 }}>
            Mot de passe réinitialisé
          </h2>
        </div>

        <p style={{ fontSize:13, color:'#374151', marginBottom:12 }}>
          Nouveau mot de passe temporaire pour <strong>{nom}</strong>.
          Communiquez-le à l'utilisateur de manière sécurisée.
        </p>

        <div style={{ background:'#F0F7FF', border:'1px solid #BFDBFE', borderRadius:8,
          padding:'12px 16px', display:'flex', alignItems:'center', gap:12, marginBottom:16 }}>
          <code style={{ flex:1, fontSize:16, fontWeight:700, color:'#1F4E79', letterSpacing:2 }}>
            {visible ? mdp : '●'.repeat(mdp.length)}
          </code>
          <button onClick={() => setVisible(v=>!v)}
            style={{ background:'none', border:'none', cursor:'pointer', color:'#6B7280' }}>
            {visible ? <EyeOff size={16}/> : <Eye size={16}/>}
          </button>
        </div>

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button className="header-btn btn-secondary" onClick={copier}
            style={{ fontSize:13, padding:'8px 16px' }}>
            Copier
          </button>
          <button className="header-btn btn-primary" onClick={onClose}
            style={{ fontSize:13, padding:'8px 16px' }}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Petit helper ── */
function Field({ label, error, children, style }) {
  return (
    <div className="form-group" style={{ margin:0, ...style }}>
      <label className="form-label">{label}</label>
      {children}
      {error && <p style={{ color:'#C00000', fontSize:11, margin:'3px 0 0' }}>{error}</p>}
    </div>
  );
}

/* ══════════════════════════════════
   ONGLET MON PROFIL
══════════════════════════════════ */
function TabProfil({ user }) {
  const [saving,  setSaving]  = useState(false);
  const [showPw,  setShowPw]  = useState(false);
  const [errors,  setErrors]  = useState({});
  const [form, setForm] = useState({
    nom_util:    user?.nom_util    || '',
    prenom_util: user?.prenom_util || '',
    email:       user?.email       || '',
    telephone:   user?.telephone   || '',
    poste:       user?.poste       || '',
    nouveau_mdp: '',
    confirm_mdp: '',
  });

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const handleSaveProfil = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.patch(`/utilisateurs/${user.id}/`, {
        nom_util: form.nom_util, prenom_util: form.prenom_util,
        email: form.email, telephone: form.telephone, poste: form.poste,
      });
      toast.success('Profil mis à jour.');
    } catch (err) {
      const d = err.response?.data;
      if (d && typeof d === 'object') setErrors(d);
      else toast.error('Erreur lors de la mise à jour.');
    } finally { setSaving(false); }
  };

  const handleChangeMdp = async (e) => {
    e.preventDefault();
    if (form.nouveau_mdp !== form.confirm_mdp) {
      setErrors({ confirm_mdp: 'Les mots de passe ne correspondent pas.' }); return;
    }
    if (form.nouveau_mdp.length < 8) {
      setErrors({ nouveau_mdp: 'Minimum 8 caractères.' }); return;
    }
    setSaving(true);
    try {
      await api.patch(`/utilisateurs/${user.id}/`, { password: form.nouveau_mdp });
      toast.success('Mot de passe modifié.');
      setForm(f => ({ ...f, nouveau_mdp: '', confirm_mdp: '' }));
    } catch { toast.error('Erreur lors du changement de mot de passe.'); }
    finally { setSaving(false); }
  };

  const fieldErr = (k) => errors[k] ? (
    <p style={{ color: '#C00000', fontSize: 11, margin: '3px 0 0' }}>
      {Array.isArray(errors[k]) ? errors[k][0] : errors[k]}
    </p>
  ) : null;

  return (
    <div style={{ maxWidth: 680 }}>
      {/* Infos profil */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ padding: '16px 20px 0', borderBottom: '1px solid #F3F4F6', marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#1F2937' }}>
            Informations personnelles
          </h3>
        </div>
        <form onSubmit={handleSaveProfil} style={{ padding: '0 20px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <Field label="Prénom *" error={errors.prenom_util}>
              <input className="form-control" value={form.prenom_util}
                onChange={e => set('prenom_util', e.target.value)} required />
            </Field>
            <Field label="Nom *" error={errors.nom_util}>
              <input className="form-control" value={form.nom_util}
                onChange={e => set('nom_util', e.target.value)} required />
            </Field>
          </div>
          <Field label="Email *" error={errors.email} style={{ marginBottom: 14 }}>
            <input className="form-control" type="email" value={form.email}
              onChange={e => set('email', e.target.value)} required />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <div>
              <label className="form-label">Téléphone</label>
              <input className="form-control" value={form.telephone}
                onChange={e => set('telephone', e.target.value)} placeholder="+225 07 00 00 00 00" />
              {fieldErr('telephone')}
            </div>
            <div>
              <label className="form-label">Poste / Fonction</label>
              <input className="form-control" value={form.poste}
                onChange={e => set('poste', e.target.value)} placeholder="Ex : Commercial senior" />
              {fieldErr('poste')}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className={`badge badge-${user?.role === 'administrateur' ? 'bleu' : 'vert'}`}>
              {user?.role === 'administrateur' ? 'Administrateur' : 'Commercial'}
            </span>
            <span className="badge badge-vert">{user?.statut || 'actif'}</span>
            <div style={{ flex: 1 }} />
            <button type="submit" className="header-btn btn-primary" disabled={saving}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Save size={14} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>

      {/* Changement mot de passe */}
      <div className="card">
        <div style={{ padding: '16px 20px 0', borderBottom: '1px solid #F3F4F6', marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#1F2937' }}>
            Changer le mot de passe
          </h3>
        </div>
        <form onSubmit={handleChangeMdp} style={{ padding: '0 20px 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            <Field label="Nouveau mot de passe *" error={errors.nouveau_mdp}>
              <div style={{ position: 'relative' }}>
                <input className="form-control" type={showPw ? 'text' : 'password'}
                  value={form.nouveau_mdp} onChange={e => set('nouveau_mdp', e.target.value)}
                  placeholder="Min. 8 caractères" style={{ paddingRight: 38 }} />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </Field>
            <Field label="Confirmer *" error={errors.confirm_mdp}>
              <input className="form-control" type={showPw ? 'text' : 'password'}
                value={form.confirm_mdp} onChange={e => set('confirm_mdp', e.target.value)}
                placeholder="Répéter le mot de passe" />
            </Field>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" className="header-btn btn-primary"
              disabled={saving || !form.nouveau_mdp}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Shield size={14} /> {saving ? 'Modification…' : 'Modifier le mot de passe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ══════════════════════════════════
   PAGE PRINCIPALE
══════════════════════════════════ */
export default function UtilisateursPage() {
  const dispatch = useDispatch();
  const { liste, loading } = useSelector(s => s.utilisateurs);
  const { user } = useSelector(s => s.auth);

  const [tab,           setTab]           = useState('liste');
  const [search,        setSearch]        = useState('');
  const [filterRoles,   setFilterRoles]   = useState(() => new Set());
  const [filterStatuts, setFilterStatuts] = useState(() => new Set());
  const [modalCreate,   setModalCreate]   = useState(false);
  const [modalEdit,     setModalEdit]     = useState(null);
  const [mdpGenere,     setMdpGenere]     = useState(null);
  const [actioning,     setActioning]     = useState({});

  const tog = (setter) => (v) => setter(s => { const n = new Set(s); n.has(v) ? n.delete(v) : n.add(v); return n; });

  const ROLE_OPTIONS = [
    { value: 'administrateur', label: 'Administrateur', color: '#1F4E79' },
    { value: 'commercial',     label: 'Commercial',     color: '#1E7145' },
  ];
  const STATUT_OPTIONS = [
    { value: 'actif',    label: 'Actif',    color: '#1E7145' },
    { value: 'suspendu', label: 'Suspendu', color: '#C00000' },
    { value: 'inactif',  label: 'Inactif',  color: '#6B7280' },
  ];

  const charger = useCallback(() => {
    const p = {};
    if (search) p.search = search;
    dispatch(fetchUtilisateurs(p));
  }, [dispatch, search]);

  useEffect(() => { charger(); }, [charger]);

  /* ── Actions ── */
  const setAction = (id, v) => setActioning(a => ({ ...a, [id]: v }));

  const handleSuspendre = async (u) => {
    if (!window.confirm(`Suspendre le compte de ${u.nom_complet} ?`)) return;
    setAction(u.id, 'suspendre');
    try {
      await dispatch(suspendreUtilisateur(u.id)).unwrap();
      toast.success(`${u.nom_complet} suspendu.`);
      charger();
    } catch (err) {
      const msg = err?.error || (Array.isArray(err) ? err[0] : 'Erreur.');
      toast.error(msg);
    } finally { setAction(u.id, false); }
  };

  const handleActiver = async (u) => {
    setAction(u.id, 'activer');
    try {
      await dispatch(activerUtilisateur(u.id)).unwrap();
      toast.success(`${u.nom_complet} activé.`);
      charger();
    } catch (err) {
      const msg = err?.error || 'Erreur.';
      toast.error(msg);
    } finally { setAction(u.id, false); }
  };

  const handleResetMdp = async (u) => {
    if (!window.confirm(`Générer un nouveau mot de passe pour ${u.nom_complet} ?`)) return;
    setAction(u.id, 'reset');
    try {
      const result = await dispatch(reinitialiserMdp(u.id)).unwrap();
      setMdpGenere({ nom: u.nom_complet, mdp: result.nouveau_mdp });
    } catch (err) {
      const msg = err?.error || 'Erreur lors de la réinitialisation.';
      toast.error(msg);
    } finally { setAction(u.id, false); }
  };

  const handleSupprimer = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${u.nom_complet} ?\nCette action est irréversible.`)) return;
    setAction(u.id, 'supprimer');
    try {
      await dispatch(supprimerUtilisateur(u.id)).unwrap();
      toast.success(`${u.nom_complet} supprimé.`);
      charger();
    } catch (err) {
      const msg = err?.error || (Array.isArray(err?.non_field_errors) ? err.non_field_errors[0] : 'Erreur.');
      toast.error(msg);
    } finally { setAction(u.id, false); }
  };

  const anyFilter = filterRoles.size > 0 || filterStatuts.size > 0;

  const filtered = liste.filter(u => {
    if (filterRoles.size   > 0 && !filterRoles.has(u.role))     return false;
    if (filterStatuts.size > 0 && !filterStatuts.has(u.statut)) return false;
    return true;
  });

  /* ── KPIs ── */
  const nbTotal    = liste.length;
  const nbAdmins   = liste.filter(u => u.role === 'administrateur').length;
  const nbCommerci = liste.filter(u => u.role === 'commercial').length;
  const nbSuspend  = liste.filter(u => u.statut !== 'actif').length;

  return (
    <div className="page-list">
      {/* ── En-tête : Tabs + bouton sur la même ligne ── */}
      <div style={{ display:'flex', alignItems:'center', borderBottom:'2px solid #E5E7EB', marginBottom: 16 }}>
        {[
          { key:'liste',  label:'Liste des utilisateurs', icon: Users  },
          { key:'profil', label:'Mon profil',             icon: Shield },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            style={{
              display:'flex', alignItems:'center', gap:7,
              padding:'9px 22px', fontSize:13, fontWeight:600,
              border:'none', background:'none', cursor:'pointer', marginBottom:-2,
              borderBottom: tab===key ? '2px solid #1F4E79' : '2px solid transparent',
              color: tab===key ? '#1F4E79' : '#6B7280',
            }}>
            <Icon size={14} /> {label}
          </button>
        ))}

        {/* Recherche + bouton — à droite sur la même ligne */}
        {tab === 'liste' && (
          <div style={{ marginLeft:'auto', display:'flex', alignItems:'center', gap:8, paddingBottom:4 }}>
            <div className="search-bar" style={{ minWidth:200 }}>
              <Search size={13} />
              <input placeholder="Nom, email…" value={search}
                onChange={e => setSearch(e.target.value)} style={{ fontSize:12 }} />
            </div>
            <div style={{ width:1, height:24, background:'#E5E7EB', flexShrink:0 }} />
            <button className="header-btn btn-primary"
              onClick={() => setModalCreate(true)}
              style={{ display:'flex', alignItems:'center', gap:6, height:34, padding:'0 14px', fontSize:13, fontWeight:700, flexShrink:0 }}>
              <Plus size={14} /> Nouvel utilisateur
            </button>
          </div>
        )}
      </div>

      {/* Onglet Mon profil */}
      {tab === 'profil' && <TabProfil user={user} />}

      {/* Onglet Liste */}
      {tab === 'liste' && <div>

      {/* ── Checkbox filters ── */}
      <div style={{ display:'flex', alignItems:'center', gap:20, flexWrap:'wrap',
        padding:'6px 12px', background:'#F9FAFB', border:'1px solid #E5E7EB',
        borderRadius:8, marginBottom:16 }}>

        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:11, color:'#6B7280', fontWeight:600, flexShrink:0 }}>Rôle :</span>
          {ROLE_OPTIONS.map(({ value, label, color }) => (
            <label key={value} style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer',
              fontSize:12, fontWeight: filterRoles.has(value) ? 700 : 400,
              color: filterRoles.has(value) ? color : '#6B7280', userSelect:'none' }}>
              <input type="checkbox" checked={filterRoles.has(value)} onChange={() => tog(setFilterRoles)(value)}
                style={{ accentColor:color, cursor:'pointer', width:14, height:14 }} />
              {label}
            </label>
          ))}
        </div>

        <div style={{ width:1, height:20, background:'#E5E7EB', flexShrink:0 }} />

        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:11, color:'#6B7280', fontWeight:600, flexShrink:0 }}>Statut :</span>
          {STATUT_OPTIONS.map(({ value, label, color }) => (
            <label key={value} style={{ display:'flex', alignItems:'center', gap:5, cursor:'pointer',
              fontSize:12, fontWeight: filterStatuts.has(value) ? 700 : 400,
              color: filterStatuts.has(value) ? color : '#6B7280', userSelect:'none' }}>
              <input type="checkbox" checked={filterStatuts.has(value)} onChange={() => tog(setFilterStatuts)(value)}
                style={{ accentColor:color, cursor:'pointer', width:14, height:14 }} />
              {label}
            </label>
          ))}
        </div>

        {anyFilter && (
          <button onClick={() => { setFilterRoles(new Set()); setFilterStatuts(new Set()); }}
            style={{ fontSize:11, color:'#C00000', background:'none', border:'none',
              cursor:'pointer', textDecoration:'underline', padding:0, marginLeft:'auto' }}>
            Tout afficher
          </button>
        )}
      </div>

      {/* ── KPIs ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:20 }}>
        {[
          { label:'Total',          value:nbTotal,    color:'#1F4E79', bg:'#EBF3FB', Icon:Users     },
          { label:'Administrateurs',value:nbAdmins,   color:'#1E7145', bg:'#E8F5E9', Icon:Shield    },
          { label:'Commerciaux',    value:nbCommerci, color:'#5B21B6', bg:'#EDE9FE', Icon:Briefcase },
          { label:'Inactifs/Susp.', value:nbSuspend,  color:'#C00000', bg:'#FCE4EC', Icon:AlertTriangle },
        ].map(k => (
          <div key={k.label} className="card" style={{ padding:'14px 18px', borderLeft:`4px solid ${k.color}`, background:k.bg }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <p style={{ fontSize:11, color:'#6B7280', fontWeight:600, textTransform:'uppercase', margin:0 }}>{k.label}</p>
              <k.Icon size={16} color={k.color} style={{ opacity:0.5 }} />
            </div>
            <p style={{ fontSize:24, fontWeight:700, color:k.color, margin:'4px 0 0' }}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* ── Tableau ── */}
      <div className="table-container">
        {loading ? (
          <div className="spinner"><div className="spinner-ring" /> Chargement…</div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ minWidth:900 }}>
              <thead>
                <tr>
                  <th>N°</th>
                  <th>Nom / Prénom</th>
                  <th>Email</th>
                  <th>Téléphone</th>
                  <th>Rôle</th>
                  <th>Statut</th>
                  <th>Dernière connexion</th>
                  <th>Connexions réussies</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign:'center', padding:32, color:'#6B7280' }}>
                      {anyFilter ? 'Aucun résultat pour ce filtre' : 'Aucun utilisateur trouvé'}
                    </td>
                  </tr>
                ) : filtered.map((u, i) => {
                  const roleInfo   = ROLES[u.role]   || { label:u.role,   badge:'gris' };
                  const statutInfo = STATUTS[u.statut] || { label:u.statut, badge:'gris' };
                  const inAction   = actioning[u.id];
                  return (
                    <tr key={u.id} style={u.statut !== 'actif' ? { opacity:0.7 } : {}}>
                      <td style={{ fontSize:12, color:'#1F4E79', fontWeight:700 }}>{i+1}</td>
                      <td>
                        <strong style={{ fontSize:13 }}>{u.prenom_util} {u.nom_util}</strong>
                      </td>
                      <td style={{ fontSize:13 }}>{u.email}</td>
                      <td style={{ fontSize:13 }}>{u.telephone || '—'}</td>
                      <td><span className={`badge badge-${roleInfo.badge}`}>{roleInfo.label}</span></td>
                      <td><span className={`badge badge-${statutInfo.badge}`}>{statutInfo.label}</span></td>
                      <td style={{ fontSize:12 }}>{fmt(u.date_derniere_cnx)}</td>
                      <td style={{ fontSize:13, color:'#1F4E79', fontWeight:600, textAlign:'center' }}>
                        {u.nb_connexions ?? '—'}
                      </td>
                      <td>
                        <div className="flex gap-8" style={{ flexWrap:'nowrap' }}>
                          {/* Modifier */}
                          <button className="header-btn btn-secondary btn-sm btn-icon"
                            onClick={() => setModalEdit(u)} title="Modifier"
                            disabled={!!inAction}>
                            <Pencil size={13} />
                          </button>

                          {/* Suspendre / Activer */}
                          {u.statut === 'actif' ? (
                            <button className="header-btn btn-warning btn-sm btn-icon"
                              onClick={() => handleSuspendre(u)} title="Suspendre"
                              disabled={!!inAction}>
                              <UserX size={13} />
                            </button>
                          ) : (
                            <button className="header-btn btn-success btn-sm btn-icon"
                              onClick={() => handleActiver(u)} title="Activer"
                              disabled={!!inAction}>
                              <UserCheck size={13} />
                            </button>
                          )}

                          {/* Reset MDP */}
                          <button className="header-btn btn-secondary btn-sm btn-icon"
                            onClick={() => handleResetMdp(u)} title="Réinitialiser le mot de passe"
                            disabled={!!inAction}>
                            <KeyRound size={13} />
                          </button>

                          {/* Supprimer */}
                          <button className="header-btn btn-danger btn-sm btn-icon"
                            onClick={() => handleSupprimer(u)} title="Supprimer"
                            disabled={!!inAction}>
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      </div>}

      {/* ── Modals ── */}
      {modalCreate && (
        <ModalUtilisateur
          utilisateur={null}
          onClose={() => setModalCreate(false)}
          onSaved={() => { setModalCreate(false); charger(); }}
        />
      )}
      {modalEdit && (
        <ModalUtilisateur
          utilisateur={modalEdit}
          onClose={() => setModalEdit(null)}
          onSaved={() => { setModalEdit(null); charger(); }}
        />
      )}
      {mdpGenere && (
        <ModalMdpGenere
          nom={mdpGenere.nom}
          mdp={mdpGenere.mdp}
          onClose={() => setMdpGenere(null)}
        />
      )}
    </div>
  );
}
